import { useState } from "react";
import { Dialog } from "@/components/ui/dialog/dialog";
import { StrokedText } from "@/components/ui/text/stroked-text";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { PokemonTypeBadge } from "@/components/pokemon/pokemon-type-badge";
import { Direction } from "types/battle-engine/battle";
import { RaidConfig } from "game/data/raids";
import { RaidDifficulty } from "types/raids/types";
import { PokeType } from "types/pokemon/poke-type";
import classNames from "classnames";
import { Button } from "@/components/ui/buttons/button";
import { isXsScreen } from "@/utils/screen-size-check";
import { parsePokeNameForSprite } from "utils/pokemon/name-utils";

interface RaidDetailModalProps {
  raid: RaidConfig;
  isOpen: boolean;
  onClose: () => void;
  onStart: (difficulty: RaidDifficulty) => void;
  isCreating: boolean;
}

const DIFFICULTY_OPTIONS: {
  value: RaidDifficulty;
  label: string;
  color: string;
  bgColor: string;
}[] = [
  {
    value: RaidDifficulty.EASY,
    label: "Easy",
    color: "text-pk-green-4",
    bgColor: "bg-pk-green-3",
  },
  {
    value: RaidDifficulty.NORMAL,
    label: "Normal",
    color: "text-pk-blue-4",
    bgColor: "bg-pk-blue-3",
  },
  {
    value: RaidDifficulty.HARD,
    label: "Hard",
    color: "text-pk-yellow-3",
    bgColor: "bg-pk-yellow-2",
  },
  {
    value: RaidDifficulty.EXPERT,
    label: "Expert",
    color: "text-pk-red-4",
    bgColor: "bg-pk-red-3",
  },
  {
    value: RaidDifficulty.EPIC,
    label: "Epic",
    color: "text-pk-psychic",
    bgColor: "bg-pk-psychic/80",
  },
];

/**
 * Formats a pokemon name for display (e.g., "charizard-mega-x" -> "Charizard Mega X")
 */
const formatPokemonName = (name: string): string => {
  return name
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Formats item name for display (e.g., "fire-stone" -> "Fire Stone")
 */
const formatItemName = (name: string): string => {
  return name
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const RaidDetailModal = ({
  raid,
  isOpen,
  onClose,
  onStart,
  isCreating,
}: RaidDetailModalProps) => {
  const [selectedDifficulty, setSelectedDifficulty] = useState<RaidDifficulty>(
    RaidDifficulty.NORMAL,
  );

  const bosses = raid.bosses;

  const handleStart = () => {
    onStart(selectedDifficulty);
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <div
        className="relative w-[95vw] max-w-md max-h-[90vh] rounded-xl overflow-hidden border-4 border-pk-gray-4 shadow-cartoon flex flex-col"
        style={{
          backgroundImage: `url("/assets/raids/backgrounds/${raid.type.toLowerCase()}.webp")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black/85" />

        {/* Content */}
        <div className="relative z-10 flex flex-col h-full overflow-hidden">
          {/* Header */}
          <div className="p-4 pb-2">
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-1 gap-4 items-center">
                <PokemonTypeBadge type={raid.type as PokeType} hideText />
                <StrokedText
                  fontSize={isXsScreen() ? 20 : 28}
                  strokeWidth={8}
                  strokeColor="#1a1a1a"
                  fillColor="#ffffff"
                  className="block mt-2"
                >
                  {raid.name}
                </StrokedText>
              </div>
            </div>
            <p className="text-white/80 text-xs xs:text-sm mt-2 leading-relaxed">
              {raid.description}
            </p>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-4">
            {/* Difficulty Picker */}
            <div>
              <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-2">
                Difficulty
              </h3>
              <div className="flex flex-wrap gap-2">
                {DIFFICULTY_OPTIONS.map((diff) => (
                  <button
                    key={diff.value}
                    onClick={() => setSelectedDifficulty(diff.value)}
                    className={classNames(
                      "px-2 py-1 rounded-lg font-bold text-xs xs:text-sm transition-all",
                      "border-2",
                      {
                        [`${diff.bgColor} border-white/30 ${diff.color} scale-105`]:
                          selectedDifficulty === diff.value,
                        "bg-black/40 border-white/10 text-white/60 hover:bg-black/50 hover:text-white/80":
                          selectedDifficulty !== diff.value,
                      },
                    )}
                  >
                    {diff.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Possible Bosses */}
            <div>
              <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-2">
                Possible Bosses ({bosses.length})
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {bosses.map((boss, idx) => (
                  <div
                    key={`${boss.name}-${idx}`}
                    className="flex flex-col items-center bg-black/40 rounded-lg p-2 border border-white/10"
                  >
                    <div className="w-12 h-9 flex items-center justify-center">
                      <PokemonSprite
                        pokemon={{
                          base_pokemon_name: parsePokeNameForSprite(boss.name),
                        }}
                        animation="Idle"
                        direction={Direction.S}
                        className="scale-125 xs:scale-150"
                      />
                    </div>
                    <span className="text-white/80 text-[10px] text-center leading-tight mt-1 line-clamp-2">
                      {formatPokemonName(boss.name)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Item Drops */}
            {raid.itemDrops.length > 0 && (
              <div>
                <h3 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-2">
                  Possible Item Drops
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  {raid.itemDrops.map((drop, idx) => (
                    <div
                      key={`${drop.item_name}-${idx}`}
                      className="flex flex-col items-center gap-2 bg-black/40 rounded-lg px-3 py-2 border border-white/10"
                    >
                      <img
                        src={`/assets/items/${drop.item_name}.png`}
                        alt={drop.item_name}
                        className="w-8 h-8 object-contain"
                      />
                      <div className="flex flex-col">
                        <span className="text-white/90 text-xs font-medium text-center">
                          {formatItemName(drop.item_name)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No drops message */}
            {raid.itemDrops.length === 0 && (
              <div className="bg-black/30 rounded-lg p-3 border border-white/10">
                <p className="text-white/50 text-sm text-center">
                  No special item drops for this raid
                </p>
              </div>
            )}
          </div>

          {/* Footer - Start Button */}
          <div className="flex p-2 items-center justify-between gap-2 bg-gradient-to-t from-black/60 to-transparent">
            <div className="grow text-xs xs:text-sm  ">
              <Button
                variant="green"
                onClick={handleStart}
                loading={isCreating}
              >
                {isCreating ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Creating...
                  </span>
                ) : (
                  "Start Raid"
                )}
              </Button>
            </div>

            <Button
              variant="red"
              onClick={onClose}
              className="text-xs xs:text-sm"
            >
              ✕ Cancel
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
